'use client';

import cn from 'classnames';
import RedundantLink from 'components/accesibility/redundant-link';
import { HoldingTypeBadges } from 'components/collection/holding-type-badges';
import NoImage from 'components/helper/no-image';
import LegacyOrMarkdownParser from 'components/rich-text/legacy-or-markdown-parser';
import {
	lofiArtworkObjectModel,
	type LofiArtworkObjectModelInput,
} from 'helpers/collection';
import {
	type CSSProperties,
	type MouseEvent,
	type ReactElement,
	type Ref,
} from 'react';
import GTMLink from 'components/controls/links/GTMLink';
import styles from './collection-object.module.scss';
import { DebuggingInfo, type IDebuggingInfo } from './debugging-info';
import useAnimateIntoView from 'hooks/use-animate-into-view';
import { mergeRefs } from 'react-merge-refs';
import hoverStyles from '../../cards/hover-state.module.scss';
const BaseCollectionObject = ({
	object,
	handleObjectClick,
	index,
	enforceMaxWidth = false,
	lazyLoad = true,
	url,
	body,
	highlights,
	score,
	elasticSearchId,
	isLoading,
	handleExplain,
	explanation,
	showSearchDebug = false,
	analytics,
	ref,
	transparentBackground = false,
	className,
	style,
}: IProps) => {
	const formattedObject = lofiArtworkObjectModel(object);
	const hasImage =
		formattedObject?.image && !formattedObject?.image.includes('NoImage');
	const {
		ref: animateRef,
		className: animateClassName,
		style: animateStyle,
	} = useAnimateIntoView({ isLoading });
	return (
		<figure
			ref={mergeRefs([ref, animateRef])}
			style={{ ...style, ...animateStyle }}
			className={cn(
				hoverStyles.card,
				styles.collectionObject,
				'collection-object',
				animateClassName,
				className,
			)}
			data-accession-number={formattedObject.accessionNumber}
		>
			<div
				className={cn(styles.imageWrapper, {
					[styles.transparentBackground]: transparentBackground,
				})}
			>
				<RedundantLink
					prefetch={false}
					href={url}
					onClick={handleObjectClick && ((e) => handleObjectClick(e, index))}
					title={formattedObject.title}
					Content_Type={analytics?.Content_Type}
					Content_Name={formattedObject.title}
					Content_Block={analytics?.Content_Block}
					event={analytics?.event}
					Object_ID={`${formattedObject.objectID}`}
				/>
				{hasImage ? (
					/* eslint-disable @next/next/no-img-element */
					<img
						loading={lazyLoad ? 'lazy' : 'eager'}
						className={cn(styles.image, { [styles.gridView]: enforceMaxWidth })}
						src={formattedObject.image}
						srcSet={formattedObject.imageSrcSet}
						alt={formattedObject.imageAlt}
					/>
				) : (
					/* eslint-enable @next/next/no-img-element */
					<NoImage />
				)}
			</div>
			<HoldingTypeBadges
				extendedStatuses={formattedObject.extendedStatuses}
				variant={'small'}
			/>
			<figcaption className={styles.caption}>
				<div className={styles.title}>
					<GTMLink
						prefetch={false}
						onClick={handleObjectClick && ((e) => handleObjectClick(e, index))}
						className={styles.link}
						href={url}
						title={formattedObject.title}
						Content_Type={analytics?.Content_Type}
						Content_Name={formattedObject.title}
						Content_Block={analytics?.Content_Block}
						event={analytics?.event}
						Object_ID={`${formattedObject.objectID}`}
					>
						<LegacyOrMarkdownParser>
							{formattedObject.title}
						</LegacyOrMarkdownParser>
					</GTMLink>
				</div>
				<div className={styles.body}>{body}</div>
				{showSearchDebug && (
					<DebuggingInfo
						elasticSearchId={elasticSearchId}
						explanation={explanation} // just prop drilling
						handleExplain={handleExplain}
						/** @ts-expect-error attr doesn't exist on every type */
						yearFrom={object?.yearRange?.gte}
						/** @ts-expect-error attr doesn't exist on every type */
						yearTo={object?.yearRange?.lte}
						score={score}
						highlights={highlights}
						accessionNumber={formattedObject.accessionNumber}
						sortArtist={object['sortArtist']}
					/>
				)}
			</figcaption>
		</figure>
	);
};

export interface IProps extends IDebuggingInfo {
	body?: ReactElement;
	enforceMaxWidth?: boolean;
	handleObjectClick?: (e: MouseEvent, index?: number) => void;
	index?: number;
	isLoading?: boolean; /* specifically for use in a search context */
	lazyLoad?: boolean;
	object: LofiArtworkObjectModelInput;
	url: string;
	/** @remarks this card isn't always used in a search context, so its nice to show/hide this, even in dev */
	showSearchDebug?: boolean;
	analytics?: {
		Content_Name?: string;
		Content_Type?: string;
		Content_Block?: string;
		event?: string;
	};
	ref?: Ref<HTMLElement>;
	transparentBackground?: boolean;
	className?: string;
	style?: CSSProperties;
}

export default BaseCollectionObject;
